import { getScanOrderShippingLabelUsage } from "@/apis/tms/order";
import { TMSShippingLabelMode } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { Spin } from "antd";
import classNames from "classnames";
import { forwardRef, useImperativeHandle, useMemo } from "react";

interface ShippingLabelInfoProps {
  className?: string;
  orderId: string;
}

export interface ShippingLabelInfoRef {
  refresh: () => void;
}

const useShippingLabelInfo = (orderId: string) => {
  return useQuery({
    queryKey: [`order-${orderId}-shipping-label-usage`],
    queryFn: () => getScanOrderShippingLabelUsage(orderId),
    staleTime: 0,
  });
};

const ShippingLabelInfo = forwardRef<
  ShippingLabelInfoRef,
  ShippingLabelInfoProps
>((props, ref) => {
  const { className, orderId } = props;
  const {
    data: shippingLabelUsage,
    isFetching,
    refetch,
  } = useShippingLabelInfo(orderId);

  const enabledCartonShippingLabel = useMemo(() => {
    if (!shippingLabelUsage) return false;
    return shippingLabelUsage.shippingLabelMode === TMSShippingLabelMode.Carton;
  }, [shippingLabelUsage]);

  const usedCount = useMemo(() => {
    if (!shippingLabelUsage) return 0;
    if (shippingLabelUsage.shippingLabelMode === TMSShippingLabelMode.Pallet) {
      return shippingLabelUsage.palletCount;
    } else {
      return shippingLabelUsage.scannedCartonCount;
    }
  }, [shippingLabelUsage]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: refetch,
    }),
    [refetch],
  );

  return (
    <>
      {enabledCartonShippingLabel ? (
        <Spin spinning={isFetching} size="small">
          <div
            className={classNames(
              "flex flex-row items-center gap-2",
              "border-[1px] border-gray-200 rounded-md px-3",
              "bg-blue-500 text-white",
              className,
            )}
          >
            <div>Used: {usedCount}</div>
            <div className="rotate-[30deg] pb-1">|</div>
            <div>Total: {shippingLabelUsage!.totalLabelsCount}</div>
            <div>{shippingLabelUsage!.shippingLabelMode} Shipping Labels</div>
          </div>
        </Spin>
      ) : null}
    </>
  );
});

export default ShippingLabelInfo;
